import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PrimaryButton from "../components/PrimaryButton";
import toast from "react-hot-toast";
import { MobileResponsive } from "./MobileResponsive";
import FloatingInput from "../components/FloatingInput";
import { useDispatch, useSelector } from "react-redux";
import { loginService, setPassword } from "../features/authSlice";
import DynamicMetaTags from "../components/DynamicMetaTags";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });

  // Efficient handler to update form data
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent form reload

    // Validate if both fields are filled
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setErrors({ email: "", password: "" });

    try {
      // Dispatch the login action and await result
      const resultAction = await dispatch(loginService(formData));

      // Check if login was successful
      if (loginService.fulfilled.match(resultAction)) {
        dispatch(setPassword(formData.password));
        navigate("/"); // Navigate to home after successful login
      } else {
        // Handle login errors (display appropriate message)
        const errorMsg = resultAction.payload;
        const errorMessage =
          typeof errorMsg === "object" ? errorMsg.message : errorMsg;

        // Handle specific error messages and set form errors
        if (errorMessage?.toLowerCase().includes("admin does not exist")) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            email: "Email doesn't exist",
          }));
        } else if (errorMessage?.toLowerCase().includes("email")) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            email: errorMessage || "Invalid email",
          }));
        } else if (errorMessage?.toLowerCase().includes("credentials")) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            password: errorMessage || "Invalid password",
          }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            password: "Password must be of 8 characters",
          }));
        }
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
    }
  };

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Login to Your Real-Time Logistics Dashboard"
        pageDescription="Get real-time insights with Beep's dashboard! Monitor active drivers, food outlets, hubs, and detailed analytics on orders. Track Pending, Delivering, and Done orders seamlessly across outlets and hubs."
        pageKeywords="Beep, logistics dashboard, real-time analytics, active drivers, active food outlets, active hubs, order tracking, pending orders, delivering orders, completed orders, logistics insights"
      />
      <MobileResponsive />
      <div className="relative lg:flex hidden items-center justify-center h-screen">
        <div>
          <div className="flex flex-col items-center justify-center gap-5 absolute top-7 left-[4.7vw]">
            <img src="/assets/logo.svg" className="w-32" alt="" />
          </div>
          <div className="pt-7 pb-5 text-center">
            <h1 className="text-[30px] font-bold tracking-wide text-black">
              Welcome back!
            </h1>
            <p className="text-[#4C4C4C] text-lg my-3 font-normal tracking-widest">
              Log in to your Beep Admin Portal
            </p>
          </div>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-6 pt-3 2xl:w-[21vw] xl:w-[24vw] lg:w-[26vw] m-auto"
          >
            <FloatingInput
              name="email"
              id="email"
              type="email"
              placeholder="Enter Email"
              value={formData.email} // Controlled input
              error={errors.email}
              handleBlur={handleInputChange} // Unified change handler
              onChange={handleInputChange} // Pass to handle live input change
            />

            <FloatingInput
              name="password"
              type="password"
              placeholder="Password"
              value={formData.password} // Controlled input
              error={errors.password}
              handleBlur={handleInputChange} // Unified change handler
              onChange={handleInputChange} // Pass to handle live input change
              id="password"
            />

            <Link
              to="/forgot-password"
              className="font-semibold text-primary text-right -mt-2 z-20 2xl:text-sm xl:text-sm lg:text-xs"
            >
              Forgot Password?
            </Link>

            <div className="flex justify-center items-center">
              <PrimaryButton
                text={loading ? "Loading.." : "Login"}
                type="submit"
                uppercase={false}
                width="w-[50%]"
              />
            </div>
          </form>

          {/* {loading && <p>Loading...</p>}
          {error && <p style={{ color: "red" }}>{error.message}</p>}
          {user && <p>Welcome, {user.name}!</p>} */}
        </div>
        <img
          src="/assets/illus1.svg"
          className="w-full absolute bottom-0 -z-20"
          alt=""
        />
      </div>
    </>
  );
};

export default Login;
